import {
  Button,
  Card,
  Flexbox,
  Grid,
  Icon,
  Input,
  Spacer,
  Spinner,
  useModal,
} from '@components/common'
import { useState } from 'react'
import { StagedAttachment, AttachmentType } from '@api/attachments/models/Attachment.model'
import ImportInput from '@components/ImportData/ImportInput'
import EditAttachmentForm from '@components/attachment/EditAttachmentForm'
import { download } from '@utils/basic'
import { AttachmentApi } from '@api'
import { toast } from '@components/common/Toasts'
import {
  ReactSelectOption,
  ReactSelectOptions,
} from '@components/common/Select/Select.types'
import { Attachment, DeleteAttachmentPayload } from '@api/models'

// Generate simple unique IDs without external dependency
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2)
}

// Generic attachment hook interface
interface AttachmentHookResult {
  selectOptions: ReactSelectOptions
  options: Attachment[]
  isLoading: boolean
  selectedOption: ReactSelectOption
  handleAttachmentSelection: (option: ReactSelectOption) => void
  attachment: {
    isLoading: boolean
    url: string
    details: Attachment
  } | null
  deleteAttachment: (params: DeleteAttachmentPayload) => Promise<void>
  isAttachmentDeleting: (attachmentId: string) => boolean
}

// Configuration for different entity types
interface EntityConfig {
  entityName: string // e.g., "Policy", "Maintenance", "Vendor"
  entityIdLabel: string // e.g., "Policy ID", "Maintenance ID", "Vendor ID"
  saveMessage: string // e.g., "insurance policy", "maintenance contract", "vendor"
}

interface StagedAttachmentEditorProps<T = any> {
  entityID: string
  entityConfig: EntityConfig
  canEdit?: boolean
  stagedAttachments: StagedAttachment[]
  onStagedAttachmentsChange: (attachments: StagedAttachment[]) => void
  attachmentType?: AttachmentType
  useAttachmentHook: (params: T) => AttachmentHookResult
  attachmentHookParamName: string // e.g., "insuranceID", "maintenanceID", "vendorID"
  maxAttachments?: number
}

const StagedAttachmentEditor = <T extends { [key: string]: string }>({
  entityID,
  entityConfig,
  canEdit = true,
  stagedAttachments,
  onStagedAttachmentsChange,
  attachmentType = 'Asset',
  useAttachmentHook,
  attachmentHookParamName,
  maxAttachments,
}: StagedAttachmentEditorProps<T>) => {
  const [currentFile, setCurrentFile] = useState<File | null>(null)
  const [currentComment, setCurrentComment] = useState('')
  const [editingAttachment, setEditingAttachment] = useState<any>(null)

  // Create the hook parameters dynamically
  const hookParams = { [attachmentHookParamName]: entityID } as T
  const {
    isLoading,
    selectOptions,
    options,
    deleteAttachment,
    isAttachmentDeleting,
  } = useAttachmentHook(hookParams)

  const {
    modalIsOpen: editModalIsOpen,
    triggerModal: triggerEditModal,
    closeModal: closeEditModal,
  } = useModal()

  const totalAttachments = selectOptions.length + stagedAttachments.length
  const hasReachedLimit = maxAttachments !== undefined && totalAttachments >= maxAttachments

  function handleFileSelection(e: any) {
    const maxSizeInBytes = 5 * 1024 * 1024 // 5MB
    if (e.target.files[0].size > maxSizeInBytes) {
      toast.error('File size should be 5 MB or less')
      return
    }

    const selectedFile = e.target.files[0]
    setCurrentFile(selectedFile)

    // Automatically fill the description with the file name
    if (selectedFile && !currentComment.trim()) {
      setCurrentComment(selectedFile.name)
    }
  }

  function handleFileRemoval() {
    setCurrentFile(null)
    setCurrentComment('')
  }

  function handleCommentChange(e: any) {
    setCurrentComment(e.target.value)
  }

  function addStagedAttachment() {
    if (!currentFile || !currentComment.trim()) return

    if (maxAttachments !== undefined && totalAttachments >= maxAttachments) {
      const limitMessage =
        maxAttachments === 1
          ? `Only one attachment is allowed per ${entityConfig.entityName.toLowerCase()}`
          : `Only ${maxAttachments} attachments are allowed per ${entityConfig.entityName.toLowerCase()}`
      toast.error(limitMessage)
      return
    }

    const newStagedAttachment: StagedAttachment = {
      id: generateId(),
      file: currentFile,
      comment: currentComment.trim(),
    }

    onStagedAttachmentsChange([...stagedAttachments, newStagedAttachment])

    // Reset form
    setCurrentFile(null)
    setCurrentComment('')
  }

  function removeStagedAttachment(id: string) {
    onStagedAttachmentsChange(stagedAttachments.filter(att => att.id !== id))
  }

  if (!entityID || entityID.trim() === '') {
    return (
      <Card>
        <Flexbox alignItems="center" justifyContent="center" height={240}>
          <span>Enter a {entityConfig.entityIdLabel} to add attachments.</span>
        </Flexbox>
      </Card>
    )
  }

  if (isLoading) return <Spinner />

  return (
    <>
      {selectOptions.length > 0 && (
        <>
          <h4>
            {maxAttachments === 1
              ? 'Existing Attachment'
              : `Existing Attachments (${selectOptions.length})`}
          </h4>
          <Spacer y="default" />

          {selectOptions.map((option, index) => {
            const attachmentDetails = options.find(att => att.FileName === option.value)
            return (
              <>
                <Card key={option.value}>
                  <Flexbox justifyContent="space-between" alignItems="center">
                    <Flexbox alignItems="center" gap={12}>
                      <Icon name="File" />
                      <div>
                        <strong>{option.label || 'Unknown file'}</strong>
                      </div>
                    </Flexbox>
                    <Flexbox gap={8}>
                      <Button
                        size="compact"
                        icon={<Icon name="Download" />}
                        onClick={async () => {
                          try {
                            const response = await AttachmentApi.getInstance().download(
                              option.value
                            )
                            const url = URL.createObjectURL(new Blob([response.data]))
                            download(url, option.value)
                          } catch (error) {
                            console.error('Download failed:', error)
                          }
                        }}
                      >
                        Download
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            size="compact"
                            icon={<Icon name="Edit" />}
                            onClick={() => {
                              if (attachmentDetails) {
                                setEditingAttachment({
                                  ...attachmentDetails,
                                  Type: attachmentType,
                                })
                                triggerEditModal()
                              }
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="compact"
                            intent="danger"
                            icon={<Icon name="Trash2" />}
                            onClick={() => {
                              if (attachmentDetails) {
                                deleteAttachment({
                                  type: attachmentType,
                                  id: attachmentDetails.ID,
                                })
                              }
                            }}
                            loading={isAttachmentDeleting(attachmentDetails?.ID)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </Flexbox>
                  </Flexbox>
                </Card>
                {index !== selectOptions.length - 1 && <Spacer y="default" />}
              </>
            )
          })}

          {editingAttachment && (
            <EditAttachmentForm
              modalIsOpen={editModalIsOpen}
              closeModal={() => {
                closeEditModal()
                setEditingAttachment(null)
              }}
              defaultValues={editingAttachment}
            />
          )}

          <Spacer y="large" />
        </>
      )}

      {!hasReachedLimit && (
        <>
          <h4>{maxAttachments === 1 ? 'Add Attachment' : 'Add New Attachment'}</h4>
          <Spacer y="default" />

          <Card>
            <Grid>
              <Grid.Item>
                <Input
                  label="Attachment Description"
                  placeholder="Enter a description for this attachment..."
                  value={currentComment}
                  onChange={handleCommentChange}
                  disabled={!canEdit}
                />
              </Grid.Item>
              <Grid.Item>
                <ImportInput
                  file={currentFile}
                  onChange={handleFileSelection}
                  onRemove={handleFileRemoval}
                  displayName={currentComment.trim() || undefined}
                  uploadDesc={
                    currentFile
                      ? 'File selected - click to change'
                      : 'Click here to select your attachment'
                  }
                  acceptedFormatsDesc="Accepted formats: .pdf, .doc, .docx, .xls, .xlsx, .csv, .zip, .png, .jpg, .jpeg, .jfif (max 5MB)"
                />
              </Grid.Item>

              <Grid.Item>
                <Flexbox justifyContent="flex-end" gap={8}>
                  <Button
                    appearance="primary"
                    icon={<Icon name="Plus" />}
                    onClick={addStagedAttachment}
                    disabled={!currentFile || !currentComment.trim() || !canEdit}
                  >
                    {stagedAttachments.length > 0 ? 'Add Another' : 'Add Attachment'}
                  </Button>
                </Flexbox>
              </Grid.Item>
            </Grid>
          </Card>
        </>
      )}

      {hasReachedLimit && stagedAttachments.length === 0 && (
        <>
          <h4>{maxAttachments === 1 ? 'Add Attachment' : 'Add New Attachment'}</h4>
          <Spacer y="default" />
          <Card>
            <Flexbox alignItems="center" justifyContent="center">
              <span style={{ color: '#666', fontStyle: 'italic' }}>
                {maxAttachments === 1
                  ? `Only one attachment is allowed per ${entityConfig.entityName.toLowerCase()}. Delete the existing attachment to add a new one.`
                  : `Only ${maxAttachments} attachments are allowed per ${entityConfig.entityName.toLowerCase()}. Delete existing attachments to add new ones.`}
              </span>
            </Flexbox>
          </Card>
        </>
      )}

      {stagedAttachments.length > 0 && (
        <>
          <Spacer y="default" />
          <h4>
            {maxAttachments === 1
              ? 'Staged Attachment'
              : `Staged Attachments (${stagedAttachments.length})`}
          </h4>
          <Spacer y="default" />

          {stagedAttachments.map(staged => (
            <Card key={staged.id}>
              <Flexbox justifyContent="space-between" alignItems="center">
                <Flexbox alignItems="center" gap={12}>
                  <Icon name="File" />
                  <strong>{staged.comment}</strong>
                </Flexbox>
                {canEdit && (
                  <Button
                    intent="danger"
                    size="compact"
                    icon={<Icon name="X" />}
                    onClick={() => removeStagedAttachment(staged.id)}
                  >
                    Remove
                  </Button>
                )}
              </Flexbox>
            </Card>
          ))}

          <Spacer y="default" />
          <Card>
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              {maxAttachments === 1
                ? `This attachment will be uploaded when you save the ${entityConfig.saveMessage}.`
                : `These attachments will be uploaded when you save the ${entityConfig.saveMessage}.`}
            </p>
          </Card>
        </>
      )}
    </>
  )
}

export default StagedAttachmentEditor
